import apiClient from '@shared/lib/general/apiClient';
import { PARTNER_DETAIL_CHANGE_VALUE } from '@/store/constants/mutationTypes';
import MapType from '@shared/lib/constant/MapType';
import {
  SERVICE_TITAN_CHANGE_VALUE,
  SERVICE_TITAN_CHANGE_VALUES,
  SETTING_CHANGE_VALUE,
} from '../constants/mutationTypes';

function handleToast(message, status, callback) {
  callback('SET_SNACKBAR', {
    show: true,
    header: '',
    status,
    message,
    timer: 10,
  });
}

const actions = {
  changeServiceTitanValue({ commit }, payload) {
    const { field, value } = payload;
    commit({
      type: SERVICE_TITAN_CHANGE_VALUE,
      field,
      value,
    });
  },
  async getServices({ rootState, commit }) {
    try {
      const {
        admin: {
          current: { corporationId: corpId },
        },
      } = rootState;
      const { data } = await apiClient({
        method: 'GET',
        url: `/servicetitan/services/${corpId}`,
      });
      commit({
        type: SERVICE_TITAN_CHANGE_VALUES,
        changes: [
          {
            field: 'services',
            value: data,
          },
        ],
      });
      return data;
    } catch (err) {
      console.error(err);
      return [];
    }
  },
  async getJobTypes({ commit, rootState }, payload) {
    try {
      const {
        admin: {
          current: { corporationId: corpId },
        },
      } = rootState;
      let id = corpId;
      if (payload && payload.corpId) {
        id = payload.corpId;
      }
      const { data } = await apiClient({
        method: 'GET',
        url: `/servicetitan/jobTypes/${id}`,
      });
      commit({
        type: SERVICE_TITAN_CHANGE_VALUES,
        changes: [
          {
            field: 'jobTypes',
            value: data,
          },
        ],
      });
    } catch (err) {
      console.error(err);
      return [];
    }
  },
  async getServiceConditionList({ commit, rootState }) {
    const { data } = await apiClient({
      method: 'POST',
      url: '/services/condition/list',
      data: {},
    });
    const {
      admin: {
        partnerSettings: { SERVICES_OFFERED },
      },
    } = rootState;
    const includedOnly = [];
    SERVICES_OFFERED?.split(',').forEach((element) => {
      includedOnly.push(...data.filter((x) => x.category === element));
    });
    commit({
      type: SERVICE_TITAN_CHANGE_VALUES,
      changes: [
        {
          field: 'serviceConditions',
          value: includedOnly,
        },
      ],
    });
  },
  async getMembershipTypePlans({ commit, rootState }, payload) {
    try {
      const {
        admin: {
          current: { corporationId: corpId },
        },
      } = rootState;
      let id = corpId;
      if (payload && payload.corpId) {
        id = payload.corpId;
      }
      const { data } = await apiClient({
        method: 'GET',
        url: `/servicetitan/membershipTypes/${id}`,
      });
      commit({
        type: SERVICE_TITAN_CHANGE_VALUES,
        changes: [
          {
            field: 'membershipTypes',
            value: data,
          },
        ],
      });
    } catch (err) {
      console.error(err);
      return [];
    }
  },
  async getSTTagTypeId({ rootState }, tagName) {
    try {
      const {
        admin: {
          current: { corporationId: corpId },
        },
      } = rootState;
      const { data: tagTypeList } = await apiClient({
        method: 'GET',
        url: `/servicetitan/tagType/${corpId}/verify?tag=${tagName}`,
      });
      return tagTypeList?.length && tagTypeList[0].id;
    } catch (e) {
      return false;
    }
  },
  async getSTCustomFieldId({ rootState }, customField) {
    try {
      const {
        admin: {
          current: { corporationId: corpId },
        },
      } = rootState;
      const { data: customFieldList } = await apiClient({
        method: 'GET',
        url: `/servicetitan/customField/${corpId}/verify?field=${customField}`,
      });

      return customFieldList?.length && customFieldList[0].id;
    } catch (e) {
      return false;
    }
  },
  async getStAvailabilitySetting({ rootState, commit, getters }) {
    try {
      commit({
        type: SETTING_CHANGE_VALUE,
        field: 'loadingSettings',
        value: true,
      });
      const {
        admin: {
          current: { corporationId: corpId },
        },
      } = rootState;
      const { isSmacAdmin } = getters;

      const { data: partnerSetting } = await apiClient({
        method: 'GET',
        url: `/partner/settings/${corpId}`,
      });

      if (isSmacAdmin) {
        commit({
          type: PARTNER_DETAIL_CHANGE_VALUE,
          field: 'partnerSettings',
          value: partnerSetting,
        });
      } else {
        commit({
          type: SETTING_CHANGE_VALUE,
          field: 'partnerSettings',
          value: partnerSetting,
        });
      }
      return partnerSetting;
    } catch (err) {
      console.error(err);
      return {};
    } finally {
      commit({
        type: SETTING_CHANGE_VALUE,
        field: 'loadingSettings',
        value: false,
      });
    }
  },
  async queryMappingsByCorpId({ rootState, commit }) {
    try {
      commit({
        type: SERVICE_TITAN_CHANGE_VALUES,
        changes: [
          {
            field: 'loadingMappings',
            value: true,
          },
        ],
      });

      const {
        admin: {
          current: { corporationId: corpId },
        },
      } = rootState;
      const { data } = await apiClient({
        method: 'POST',
        url: `/integration/mappings/query/${corpId}`,
        data: {},
      });
      const ccMappingList = [];
      const tuneupMappingList = [];
      const jobTypeMappingList = [];
      const membershipTypeMappingList = [];
      let customFieldMapping = {};
      let tagTypeMapping = {};

      if (data && data.length) {
        data.forEach((mapping) => {
          if (mapping.mapType === MapType.JOB_TYPE) {
            jobTypeMappingList.push(mapping);
          }
          if (mapping.mapType === MapType.CC_DEDUCTION) {
            ccMappingList.push(mapping);
          }
          if (mapping.mapType === MapType.MEMBERSHIP) {
            membershipTypeMappingList.push(mapping);
          }
          if (mapping.mapType === MapType.TUNEUP_JOB_TYPE) {
            tuneupMappingList.push(mapping);
          }
          if (mapping.mapType === MapType.COMFORT_CREDIT_BALANCE) {
            customFieldMapping = mapping;
          }
          if (mapping.mapType === MapType.TAG) {
            tagTypeMapping = mapping;
          }
        });
      }

      commit({
        type: SERVICE_TITAN_CHANGE_VALUES,
        changes: [
          {
            field: 'customFieldMapping',
            value: customFieldMapping,
          },
          {
            field: 'tagTypeMapping',
            value: tagTypeMapping,
          },
          {
            field: 'invoiceMappings',
            value: ccMappingList,
          },
          {
            field: 'tuneupMappings',
            value: tuneupMappingList,
          },
          {
            field: 'membershipMappings',
            value: membershipTypeMappingList,
          },
          {
            field: 'jobTypeMappings',
            value: jobTypeMappingList,
          },
          {
            field: 'loadingMappings',
            value: false,
          },
        ],
      });
    } catch (err) {
      console.error(err);
    }
  },
  async saveMapping({ rootState, commit }, payload) {
    try {
      const {
        admin: {
          current: { corporationId: corpId },
        },
      } = rootState;
      await apiClient({
        method: 'POST',
        url: `/integration/mappings/${corpId}`,
        data: payload,
      });
      const message = 'Your integration settings have been saved.';
      handleToast(message, '', commit);
    } catch (err) {
      handleToast(err, 'error', commit);
      console.error(err);
    }
  },
  async setMembershipTypeMappings({ rootState }, payload) {
    try {
      const {
        admin: {
          current: { corporationId: corpId },
        },
      } = rootState;
      let id = corpId;
      if (payload && payload.corpId) {
        id = payload.corpId;
      }
      await apiClient({
        method: 'POST',
        url: `/integration/mappings/${id}`,
        data: payload,
      });
    } catch (err) {
      console.error(err);
    }
  },
  async getMembershipTypeMappingsByTenant({ commit }, tenantId) {
    const { data } = await apiClient({
      method: 'POST',
      url: '/integration/mappings/query',
      data: {
        externalSystemId: tenantId,
        mapType: 'MembershipType',
      },
    });
    commit({
      type: SERVICE_TITAN_CHANGE_VALUES,
      changes: [
        {
          field: 'existingMembershipMapping',
          value: data,
        },
      ],
    });
  },
  async setJobTypeMappings({ rootState }, payload) {
    try {
      const {
        admin: {
          current: { corporationId: corpId },
        },
      } = rootState;
      let id = corpId;
      if (payload && payload.corpId) {
        id = payload.corpId;
      }
      await apiClient({
        method: 'POST',
        url: `/integration/mappings/${id}`,
        data: payload,
      });
    } catch (e) {
      console.error(e);
    }
  },
};

export default actions;
